'use client';

import { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/atoms/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/atoms/DropdownMenu/DropdownMenu';
import { cn } from '@/libs/utils/utils';
import type { TPinakesAlsoOn } from '@/services/pinakes/pinakes.types';
import { getAlsoOnName, getAlsoOnNote, getHttpUrl } from './ResourceCard.utils';
import { ResourceMenuRow } from './ResourceMenuRow';
import { ResourceSimilar } from './ResourceSimilar';
import { ResourceSourceIcon } from './ResourceSourceIcon';

const ROW_TEST_ID = 'resource-also-on-row';

interface ResourceAlsoOnProps {
  /** The card's Pinakes ID (`card.id`), what the menu's Similar entry is read for. */
  id: string;
  /** The card's `also_on`: the same thing on other services, tagged members first, then derived links. */
  alsoOn: TPinakesAlsoOn[];
  /** The card's own link (`http(s)` only), the menu's first row; `null` for none ("Also on" alone). */
  href: string | null;
  /** The URI the card's own favicon is read from. */
  uri: string;
  /** The card's own site, which the button names. */
  site: string | null;
}

/**
 * Where else the resource is: an outlined "Open in <site> ▾" pill with the site's icon (the one the
 * card's header already shows), whose menu has the card's own link first, then "Also on · N" with
 * one row per `also_on` entry (favicon, service name, and a note for derived links and archived
 * copies), then "Similar", which unfolds in the menu and is read only then. Nothing when the card
 * is on one site only: the card itself is then the link, and Similar stays on the resource page.
 *
 * The button shrinks to the room the action row leaves its actions: on a phone it drops the words
 * "Open in" (they stay in its `aria-label`) and truncates the site's name, keeping icon and chevron.
 *
 * Similar's loading, empty and error messages are announced in one live region that stays mounted
 * beside the button, outside the menu, so it exists before its text changes.
 *
 * Only `http(s)` entries become links; any other URI is listed as text.
 */
export function ResourceAlsoOn({ id, alsoOn, href, uri, site }: ResourceAlsoOnProps) {
  const labelId = useId();
  const [announcer, setAnnouncer] = useState<HTMLElement | null>(null);
  if (alsoOn.length === 0) return null;

  const ownLink = href && site ? { href, site } : null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            data-testid="resource-also-on-trigger"
            aria-label={ownLink ? `Open in ${ownLink.site}` : undefined}
            className="max-w-full min-w-0 shrink max-sm:gap-0.5 max-sm:px-2 max-sm:has-[>svg]:px-2"
          >
            {ownLink && <ResourceSourceIcon uri={uri} />}
            <span className={cn('truncate', ownLink && 'max-sm:hidden')}>
              {ownLink ? (
                <>
                  <span className="max-sm:hidden">Open in </span>
                  {ownLink.site}
                </>
              ) : (
                'Also on'
              )}
            </span>
            <ChevronDown aria-hidden="true" className="max-sm:size-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          data-testid="resource-also-on-menu"
          className="max-h-[var(--radix-dropdown-menu-content-available-height)] w-70 overflow-y-auto"
        >
          {ownLink && (
            <>
              <ResourceMenuRow uri={uri} href={ownLink.href} name={ownLink.site} note={null} testId={ROW_TEST_ID} />
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuLabel id={labelId} className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            Also on · {alsoOn.length}
          </DropdownMenuLabel>
          <DropdownMenuGroup aria-labelledby={labelId}>
            {alsoOn.map((entry, index) => (
              <ResourceMenuRow
                key={`${index}-${entry.uri}`}
                uri={entry.uri}
                href={getHttpUrl(entry.uri)}
                name={getAlsoOnName(entry)}
                note={getAlsoOnNote(entry)}
                testId={ROW_TEST_ID}
              />
            ))}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <ResourceSimilar id={id} announcer={announcer} />
        </DropdownMenuContent>
      </DropdownMenu>
      <span ref={setAnnouncer} role="status" data-testid="resource-similar-status" className="sr-only" />
    </>
  );
}
